'use client';

import React, { useMemo } from 'react';
import { CustomTooltipProps } from '@/modules/widgets/types/LineChartWidget';
import { NumberUtils } from '@/utils/NumberUtils';
import { DateUtils } from '@/utils/DateUtils';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { MultiLineTick, formatWeekLabel } from './StackBarChart';
import { MonthYearTick } from './components/MonthYearTick';
import { useContainerWidth } from '@/hooks/useContainerWidth';
import { DEFAULT_COLORS } from '@/constants/colors';
import { NoDataView } from '../NoDataView';
import { LineChartProps } from './types';

interface AngledTickProps {
  x: number;
  y: number;
  payload: any;
  maxLength?: number;
}

export const AngledTick = ({ x, y, payload, maxLength = 15 }: Partial<AngledTickProps>) => {
  const text = payload.value || "";
  const truncated = text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={4} textAnchor="end" fill="#5a717a" fontSize={12} fontFamily="Roboto, sans-serif" transform="rotate(-45)">
        <title>{text}</title>
        {truncated}
      </text>
    </g>
  );
};


/**
 * A unified line chart component that can be used for various line chart visualizations
 * 
 * @param props Component properties
 * @returns Line chart component
 */
function LineChart({ data, colors: customColors, widgetLevel = 'client', widget, compare, segment, hiddenSeries = new Set<string>(), externalCategories, widgetFiltersController }: LineChartProps) {
  const { containerWidth, containerRef } = useContainerWidth();

  // Check if we should show total series from widget metadata
  const showTotalSeries = widget?.metadata?.showTotalSeries ?? false;
  const totalSeriesLabel = widget?.metadata?.totalSeriesLabel ?? 'Total';
  const TOTAL_SERIES_COLOR = '#46636d';

  // Sort data chronologically if segments are dates
  const filteredBaseData = useMemo(() => {
    const base = data?.data || [];
    const filters = (data as any)?.metadata?.filters;

    if (!Array.isArray(filters) || filters.length === 0) {
      return base;
    }

    // widgetFiltersController is optional; if not provided just return base
    if (!widgetFiltersController) {
      return base;
    }

    const { selectedFilters } = widgetFiltersController;

    return base.filter((row: any) => {
      for (const filterConfig of filters) {
        const metric = filterConfig?.metric;
        const items = filterConfig?.items;
        if (!metric || !Array.isArray(items) || items.length === 0) continue;

        const selectedItems = selectedFilters?.[metric] || [];
        if (selectedItems.length >= items.length) continue;

        const datasetMetric = metric === 'segmentValue' ? 'segment' : metric;
        const rowValue = row?.[datasetMetric];

        const matches = selectedItems.some((item: any) => String(rowValue) === item.text);
        if (!matches) return false;
      }
      return true;
    });
  }, [data, widgetFiltersController]);

  const sortedData = useMemo(() => {
    return DateUtils.sortByDateField(filteredBaseData, 'segment');
  }, [filteredBaseData]);

  // Calculate total series data if enabled
  const dataWithTotal = useMemo(() => {
    if (!sortedData?.length) return sortedData;

    return sortedData.map(item => {
      const total = Object.entries(item).reduce((sum, [key, value]) => {
        if (key !== 'segment' && typeof value === 'number') {
          return sum + value;
        }
        return sum;
      }, 0);

      const compareItem: any = {};
      if (compare) {
        const foundCompareItem = compare.unified?.data?.find(_compareItem => _compareItem.identifiers?.segment === item.segment);
        if (foundCompareItem && foundCompareItem.previous) {
          for (const key in foundCompareItem?.previous?.values) {
            if (!Object.hasOwn(foundCompareItem?.previous?.values, key)) continue;
            const element = foundCompareItem?.previous?.values[key];

            compareItem[`previous_${key}`] = element;
          }
          compareItem["previous_segment"] = foundCompareItem.previous.segment;
        }
      }

      return {
        ...item,
        ...compareItem,
        ...(showTotalSeries ? { [totalSeriesLabel]: total } : {})
      };
    });
  }, [sortedData, showTotalSeries, totalSeriesLabel, compare]);

  // Extract categories based on widget level (use external if provided)
  const { categories, metricNames } = useMemo(() => {
    if (externalCategories) {
      let cats = [...externalCategories.categories];
      let names = [...externalCategories.metricNames];
      if (showTotalSeries) {
        cats = [...cats, totalSeriesLabel];
        names = [...names, totalSeriesLabel];
      }
      return { categories: cats, metricNames: names };
    }

    if (!data?.data?.length) return { categories: [], metricNames: [] };

    let cats: string[] = [];
    let names: string[] = [];

    if (widgetLevel === 'client' && data?.metrics?.length) {
      cats = data?.metrics.map(metric => metric.showName);
      names = data?.metrics.map(metric => metric.metricName);
    } else if ((data?.metrics?.length ?? 0) > 1) {
      // Multi-metric mode (Scenario 0): use showName for legend labels
      cats = data!.metrics!.map(m => m.showName);
      names = data!.metrics!.map(m => m.metricName);
    } else {
      // Single-metric mode (Scenario 2): data keys are channel names
      const firstItem = data?.data[0];
      const keys = Object.keys(firstItem).filter(key => key !== 'segment');
      cats = [...keys].sort((a, b) => a.localeCompare(b));
      names = [...keys].sort((a, b) => a.localeCompare(b));
    }

    if (showTotalSeries) {
      cats = [...cats, totalSeriesLabel];
      names = [...names, totalSeriesLabel];
    }

    return { categories: cats, metricNames: names };
  }, [data, widgetLevel, showTotalSeries, totalSeriesLabel, externalCategories]);

  // Use custom colors if provided, otherwise use defaults
  const colorsToUse = customColors && customColors.length > 0 ? customColors : DEFAULT_COLORS;

  // Map colors based on categories, use special color for total series
  const colors = categories.map((_, index) => {
    if (categories[index] === totalSeriesLabel) {
      return TOTAL_SERIES_COLOR;
    }
    return colorsToUse[index % colorsToUse.length];
  });

  // Filter visible categories based on hidden series
  const visibleCategories = useMemo(() => {
    return categories.filter(cat => !hiddenSeries.has(cat));
  }, [categories, hiddenSeries]);

  // Build a metric-aware formatter that appends % for percent metrics, $ for currency, etc.
  const metricFormatter = useMemo(() => NumberUtils.createMetricFormatter(data?.metrics), [data?.metrics]);

  // Y-axis formatter: if all metrics share the same type, use that type's formatting
  const yAxisFormatter = useMemo(() => {
    const types = data?.metrics?.map(m => m.metricType).filter(Boolean) || [];
    const allPercent = types.length > 0 && types.every(t => t === 'percent');
    const allCurrency = types.length > 0 && types.every(t => t === 'currency');
    if (allPercent) return (value: number) => `${value}%`;
    if (allCurrency) return (value: number) => NumberUtils.formatCurrency(value);
    return (value: number) => NumberUtils.formatWithSeparators(value);
  }, [data?.metrics]);

  if (data?.error) {
    return <div className="py-5 px-10 text-center text-gray-500">{data?.error}</div>;
  }

  if (!Array.isArray(data?.data) || !data?.data?.length) {
    return <NoDataView />;
  }

  // Check if all data values are 0, null, undefined, or non-numeric (excluding segment key)
  const hasMeaningfulData = data.data.some(item =>
    Object.entries(item).some(([key, value]) =>
      key !== 'segment' && value != null && value !== 0 && typeof value === 'number'
    )
  );

  if (!hasMeaningfulData) {
    return <NoDataView />;
  }

  const dataPointCount = data?.data?.length || 0;
  const shouldAngleLabels = containerWidth > 0 && (
    (containerWidth < 600 && dataPointCount > 4) ||
    (containerWidth < 800 && dataPointCount > 6) ||
    (dataPointCount > 8)
  );

  const formatTooltipValue = (value: number, dataKey?: string) => {
    // Strip "previous_" prefix to look up the original metric
    const metricName = dataKey?.startsWith("previous_") ? dataKey.slice(9) : dataKey;
    return metricFormatter(value, metricName);
  };

  // Custom Tooltip Component with total and hidden series filtering
  const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      // Filter out hidden series
      const visiblePayload = payload.filter((entry) => {
        const name = String(entry.name ?? "");
        return name ? !hiddenSeries.has(name) : true;
      });

      if (visiblePayload.length === 0) return null;

      const previousLabel = visiblePayload.filter((entry) => (entry as any)?.dataKey?.startsWith("previous_")).reduce((label, entry) => {
        if (!entry.payload?.["previous_segment"] || entry.payload?.["previous_segment"]?.trim() === "") return label;
        label = entry.payload?.["previous_segment"];
        return label;
      }, "");

      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="font-semibold text-sm mb-2" style={{ color: '#000' }}>{label}</p>

          <div className="space-y-1">
            {visiblePayload.filter((entry) => !(entry as any)?.dataKey?.startsWith("previous_"))?.map((entry, i) => (
              <div key={`item-${i}`} className="flex items-center gap-2 text-sm" style={{ color: '#000' }}>
                <div
                  className="w-1 h-1 rounded-[2px] flex-shrink-0"
                  style={{ backgroundColor: entry.color ?? "#5a717a" }}
                />
                <span>
                  {entry.name ?? ""}: {(() => {
                    const raw = entry.value;
                    const num = typeof raw === "number" ? raw : Number(raw);
                    if (Number.isFinite(num)) {
                      return formatTooltipValue(num, (entry as any)?.dataKey);
                    }
                    return raw == null ? "" : String(raw);
                  })()}
                </span>
              </div>
            ))}
          </div>

          {compare && <div className="mt-3">
            <p className="font-semibold text-sm mb-2" style={{ color: '#000' }}>{previousLabel}</p>

            <div className="space-y-1">
              {visiblePayload.filter((entry) => (entry as any)?.dataKey?.startsWith("previous_"))?.map((entry, i) => (
                <div key={`item-${i}`} className="flex items-center gap-2 text-sm" style={{ color: '#000' }}>
                  <div
                    className="w-1 h-1 rounded-[2px] flex-shrink-0"
                    style={{ backgroundColor: entry.color ?? "#5a717a" }}
                  />
                  <span>
                    {entry.name ?? ""}: {(() => {
                      const raw = entry.value;
                      const num = typeof raw === "number" ? raw : Number(raw);
                      if (Number.isFinite(num)) {
                        return formatTooltipValue(num, (entry as any)?.dataKey);
                      }
                      return raw == null ? "" : String(raw);
                    })()}
                  </span>
                </div>
              ))}
            </div>
          </div>
          }
        </div>
      );
    }
    return null;
  };

  // Legend Component (display only, no click handlers)
  const Legend = () => {
    const hasManySeries = categories.length > 10;
    const legendFontSize = hasManySeries ? '11px' : '14px';
    const legendGap = hasManySeries ? '0.5rem' : '1.25rem';

    return (
      <ul className="flex flex-wrap justify-start items-center gap-2 text-primary-blue list-none p-0 mb-4 ml-8" style={{ rowGap: '0.5rem' }}>
        {categories.filter(cat => !hiddenSeries.has(cat)).map((category, idx) => {
          const originalIndex = categories.indexOf(category);
          const color = colors[originalIndex % colors.length];

          return (
            <li
              key={category}
              className="flex gap-2 items-center font-normal"
              style={{ marginRight: legendGap }}
            >
              {category === totalSeriesLabel ? (
                <svg width="12" height="3" className="flex-shrink-0">
                  <line
                    x1="0"
                    y1="1.5"
                    x2="12"
                    y2="1.5"
                    stroke={color}
                    strokeWidth="2"
                  />
                </svg>
              ) : (
                <span
                  className="w-3 h-3 rounded-[3px] flex-shrink-0"
                  style={{
                    backgroundColor: color,
                  }}
                />
              )}
              <span style={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: legendFontSize,
                whiteSpace: 'nowrap',
                lineHeight: '1.2'
              }}>
                {category}
              </span>
            </li>
          );
        })}
      </ul>
    );
  };

  // Check if legends should be hidden via metadata
  const hideLegends = widget?.metadata?.chart?.hideLegends === true;
  const yAxisLabel = widget?.metadata?.["y-axis-label"];

  return (
    <div className="w-full h-full flex flex-col overflow-hidden" ref={containerRef}>
      {/* Legend - Above Chart */}
      {!hideLegends && <Legend />}

      {/* Main content with chart and summary cards side by side */}
      <div className="mt-6 flex flex-col lg:flex-row overflow-hidden flex-1">
        {/* Chart section - takes full width on mobile, adjusts for cards on desktop */}
        <div className="w-full overflow-hidden flex-1">
          {/* Desktop chart */}
          <div className="hidden min-h-96 h-full sm:block">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsLineChart
                data={dataWithTotal}
                margin={{
                  top: 20,
                  right: 60,
                  left: 30,
                  bottom: 20,
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  horizontal={true}
                  stroke="#e0e0e0"
                />

                <XAxis
                  dataKey="segment"
                  height={shouldAngleLabels ? 80 : 60}
                  tick={shouldAngleLabels ? <AngledTick /> : (props) => {
                    if (segment === 'month') {
                      return (
                        <MonthYearTick
                          {...props}
                          fontSize={12}
                          lineHeight={14}
                        />
                      );
                    }
                    const isWeek = segment === 'week';
                    const tickProps = isWeek
                      ? { ...props, payload: { ...props.payload, value: formatWeekLabel(props.payload.value) } }
                      : props;
                    return (
                      <MultiLineTick
                        {...tickProps}
                        maxCharsPerLine={isWeek ? 30 : 12}
                        maxLines={2}
                        fontSize={12}
                        lineHeight={14}
                      />
                    );
                  }}
                  interval={0}
                  tickMargin={10}
                  axisLine={{ stroke: '#e0e0e0' }}
                  tickLine={{ stroke: '#e0e0e0' }}
                />

                <YAxis
                  tick={{ fontSize: 12, fill: '#5a717a', fontFamily: 'Roboto, sans-serif', opacity: 1 }}
                  tickFormatter={yAxisFormatter}
                  tickMargin={10}
                  width={yAxisLabel ? 70 : 50}
                  axisLine={{ stroke: '#e0e0e0' }}
                  tickLine={{ stroke: '#e0e0e0' }}
                  label={yAxisLabel ? {
                    value: yAxisLabel,
                    angle: -90,
                    position: 'insideLeft',
                    dx: -10,
                    style: { fontSize: 12, fill: '#5a717a', fontFamily: 'Roboto, sans-serif', textAnchor: 'middle' }
                  } : undefined}
                />

                <Tooltip
                  content={(props) => (
                    <CustomTooltip
                      active={props.active}
                      payload={props.payload as any}
                      label={props.label ? String(props.label) : undefined}
                    />
                  )}
                />

                {visibleCategories.map((category) => {
                  const originalIndex = categories.indexOf(category);
                  const metricName = metricNames[originalIndex];
                  const isTotalSeries = category === totalSeriesLabel;
                  return (
                    <Line
                      key={category}
                      type="monotone"
                      style={{
                        ["--recharts-line-curve-strokeWidth" as any]: isTotalSeries ? "3px" : "2px",
                      }}
                      dataKey={metricName}
                      name={category}
                      stroke={colors[originalIndex % colors.length]}
                      strokeWidth={5.5}
                      dot={false}
                      activeDot={{ r: 4, stroke: colors[originalIndex % colors.length], strokeWidth: 2 }}
                      isAnimationActive={true}
                      animationDuration={1000}
                      animationEasing="ease-out"
                      hide={hiddenSeries.has(category)}
                    />
                  );
                })}

                {compare && visibleCategories.map((category) => {
                  const originalIndex = categories.indexOf(category);
                  const metricName = metricNames[originalIndex];
                  const isTotalSeries = category === totalSeriesLabel;
                  return (
                    <Line
                      key={`previous_${category}`}
                      type="monotone"
                      style={{
                        ["--recharts-line-curve-strokeWidth" as any]: isTotalSeries ? "3px" : "2px",
                      }}
                      dataKey={`previous_${metricName}`}
                      name={`Previous ${category}`}
                      opacity={0.5}
                      stroke={colors[originalIndex % colors.length]}
                      strokeWidth={5.5}
                      strokeDasharray={"8 4"}
                      dot={false}
                      activeDot={{ r: 4, stroke: colors[originalIndex % colors.length], strokeWidth: 2 }}
                      isAnimationActive={true}
                      animationDuration={1000}
                      animationEasing="ease-out"
                      hide={hiddenSeries.has(`previous_${category}`)}
                    />
                  );
                })}
              </RechartsLineChart>
            </ResponsiveContainer>
          </div>

          {/* Mobile chart */}
          <div className="h-48 sm:hidden">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsLineChart
                data={dataWithTotal}
                margin={{
                  top: 20,
                  right: 40,
                  left: 20,
                  bottom: 20,
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  horizontal={true}
                  stroke="#e0e0e0"
                />

                <XAxis
                  dataKey="segment"
                  height={shouldAngleLabels ? 80 : 60}
                  tick={shouldAngleLabels ? <AngledTick /> : (props) => {
                    if (segment === 'month') {
                      return (
                        <MonthYearTick
                          {...props}
                          fontSize={12}
                          lineHeight={14}
                        />
                      );
                    }
                    const isWeek = segment === 'week';
                    const tickProps = isWeek
                      ? { ...props, payload: { ...props.payload, value: formatWeekLabel(props.payload.value) } }
                      : props;
                    return (
                      <MultiLineTick
                        {...tickProps}
                        maxCharsPerLine={isWeek ? 30 : 12}
                        maxLines={2}
                        fontSize={12}
                        lineHeight={14}
                      />
                    );
                  }}
                  interval={0}
                  tickMargin={5}
                  axisLine={{ stroke: '#e0e0e0' }}
                  tickLine={{ stroke: '#e0e0e0' }}
                />

                <YAxis
                  tick={{ fontSize: 10, fill: '#5a717a', fontFamily: 'Roboto, sans-serif', opacity: 1 }}
                  tickFormatter={yAxisFormatter}
                  tickMargin={5}
                  width={yAxisLabel ? 55 : 40}
                  axisLine={{ stroke: '#e0e0e0' }}
                  tickLine={{ stroke: '#e0e0e0' }}
                  label={yAxisLabel ? {
                    value: yAxisLabel,
                    angle: -90,
                    position: 'insideLeft',
                    dx: -25,
                    style: { fontSize: 11, fill: '#5a717a', fontFamily: 'Roboto, sans-serif', textAnchor: 'middle' }
                  } : undefined}
                />

                <Tooltip
                  content={(props) => (
                    <CustomTooltip
                      active={props.active}
                      payload={props.payload as any}
                      label={props.label ? String(props.label) : undefined}
                    />
                  )}
                />

                {visibleCategories.map((category) => {
                  const originalIndex = categories.indexOf(category);
                  const metricName = metricNames[originalIndex];
                  const isTotalSeries = category === totalSeriesLabel;
                  return (
                    <Line
                      key={category}
                      type="monotone"
                      style={{
                        ["--recharts-line-curve-strokeWidth" as any]: isTotalSeries ? "3px" : "2px",
                      }}
                      dataKey={metricName}
                      name={category}
                      stroke={colors[originalIndex % colors.length]}
                      strokeWidth={5.5}
                      dot={false}
                      activeDot={{ r: 4, stroke: colors[originalIndex % colors.length], strokeWidth: 2 }}
                      isAnimationActive={true}
                      animationDuration={1000}
                      animationEasing="ease-out"
                      hide={hiddenSeries.has(category)}
                    />
                  );
                })}
                {compare && visibleCategories.map((category) => {
                  const originalIndex = categories.indexOf(category);
                  const metricName = metricNames[originalIndex];
                  const isTotalSeries = category === totalSeriesLabel;
                  return (
                    <Line
                      key={`previous_${category}`}
                      type="monotone"
                      style={{
                        ["--recharts-line-curve-strokeWidth" as any]: isTotalSeries ? "3px" : "2px",
                      }}
                      dataKey={`previous_${metricName}`}
                      name={`Previous ${category}`}
                      opacity={0.5}
                      stroke={colors[originalIndex % colors.length]}
                      strokeWidth={5.5}
                      strokeDasharray={"8 4"}
                      dot={false}
                      activeDot={{ r: 4, stroke: colors[originalIndex % colors.length], strokeWidth: 2 }}
                      isAnimationActive={true}
                      animationDuration={1000}
                      animationEasing="ease-out"
                      hide={hiddenSeries.has(`previous_${category}`)}
                    />
                  );
                })}
              </RechartsLineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}


LineChart.displayName = 'LineChart';

export { LineChart }